import { ChangeDetectionStrategy, Component, computed, linkedSignal, signal } from "@angular/core";
import { ModalComponent } from "../../modal/modal.component";
import { ModalHeaderComponent } from "../../modal/modal.header.component";
import type { ModalDefinition } from "../../modal/modal.map";
import { ModalData } from "../../modal/modal.service";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Budget } from "../budget/Budget";
import { OrganizationExport } from "./organization.export.types";

export type OrganizationExportModalParams = {
    context: OrganizationExport.Context;
};

@Component({
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ModalHeaderComponent, S25CheckboxModule, S25ButtonComponent, S25DropdownPaginatedModule],
    template: `
        <div class="s25-ng">
            <s25-modal-header [data]="headerModalData()"></s25-modal-header>
            <div class="modal-body">
                <p class="export-intro">{{ scopeDescription() }}</p>

                <fieldset class="export-fieldset">
                    <legend class="ngBold">Object types</legend>
                    @for (kindDef of exportKindDefinitions(); track kindDef.kind) {
                        <div class="export-kind-row">
                            <s25-ng-checkbox
                                [labelId]="'org-export-kind-' + kindDef.kind"
                                [value]="isKindEnabled(kindDef.kind)"
                                [disabled]="!kindDef.implemented"
                                [ariaLabel]="kindDef.label"
                                [onChange]="onKindChanged.bind(this, kindDef.kind)"
                            >
                                {{ kindDef.label }}
                                @if (!kindDef.implemented) {
                                    <span class="export-kind-soon"> (coming soon)</span>
                                }
                            </s25-ng-checkbox>
                            <p class="export-kind-desc">{{ kindDef.description }}</p>
                        </div>
                    }
                </fieldset>

                @if (isKindEnabled("budget")) {
                    <fieldset class="export-fieldset">
                        <legend class="ngBold">Budget sections</legend>
                        <div class="export-fiscal-year">
                            <label
                                id="org-export-fiscal-year-label"
                                class="ngBold required"
                                for="org-export-fiscal-year"
                            >
                                Fiscal year
                            </label>
                            <s25-ng-dropdown-search-criteria
                                type="fiscalYears"
                                [fieldId]="'org-export-fiscal-year'"
                                [(value)]="selectedFiscalYear"
                                placeholder="Select fiscal year"
                                ariaLabel="Fiscal year"
                                class="c-input export-fiscal-year-dropdown"
                            />
                        </div>
                        <s25-ng-checkbox
                            [labelId]="'org-export-budget-summary'"
                            [(value)]="includeOrgBudgetSummary"
                            ariaLabel="Organization budget summary"
                        >
                            Organization budget summary
                        </s25-ng-checkbox>
                        <s25-ng-checkbox
                            [labelId]="'org-export-budget-plans'"
                            [(value)]="includeBudgetPlans"
                            ariaLabel="Budget plans"
                        >
                            Budget plans
                        </s25-ng-checkbox>
                        <s25-ng-checkbox
                            [labelId]="'org-export-budget-line-items'"
                            [(value)]="includeLineItems"
                            ariaLabel="Line items"
                        >
                            Line items
                        </s25-ng-checkbox>
                    </fieldset>
                }

                @if (validationMessage()) {
                    <p class="export-validation" role="alert">{{ validationMessage() }}</p>
                }
            </div>
            <div class="modal-footer export-footer">
                <s25-ng-button type="outline" buttonText="Cancel" (clicked)="cancel()"></s25-ng-button>
                <s25-ng-button
                    type="primary"
                    buttonText="Export"
                    [disabled]="!canConfirm()"
                    (clicked)="confirm()"
                ></s25-ng-button>
            </div>
        </div>
    `,
    styles: `
        .export-intro {
            margin-bottom: 1rem;
        }

        .export-fieldset {
            border: none;
            margin: 0 0 1.25rem;
            padding: 0;
        }

        .export-kind-row {
            margin-bottom: 0.75rem;
        }

        .export-kind-desc {
            margin: 0.25rem 0 0 1.75rem;
            color: var(--color--text-muted, #6b7280);
            font-size: 0.875rem;
        }

        .export-kind-soon {
            font-style: italic;
        }

        .export-fiscal-year {
            margin-bottom: 1rem;
        }

        .export-fiscal-year-dropdown {
            display: block;
            margin-top: 0.35rem;
            max-width: 100%;
        }

        label.required::after {
            content: " *";
            color: var(--color--danger-text, #b42318);
        }

        .export-validation {
            color: var(--color--danger-text, #b42318);
            margin: 0;
        }

        .export-footer {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
            padding: 0.75rem 1rem;
            border-top: 1px solid var(--color--border, #e5e7eb);
        }
    `,
})
export class OrganizationExportModalComponent extends ModalComponent<OrganizationExportModalParams> {
    static readonly size = "md" satisfies ModalDefinition["size"];
    static readonly title = "Export organization data";

    readonly exportKindDefinitions = computed(() => OrganizationExport.KIND_REGISTRY);

    readonly context = computed(() => this.params()?.context);

    private readonly kindEnabled = signal<Partial<Record<OrganizationExport.Kind, boolean>>>({ budget: true });

    readonly selectedFiscalYear = linkedSignal<DropDownItem<number> | null>(() => {
        const exportContext = this.context();
        if (exportContext?.fiscalYearId != null) {
            return {
                itemId: exportContext.fiscalYearId,
                itemName: exportContext.fiscalYear ?? "",
                itemTypeId: Budget.BUDGET_ITEM_TYPE_ID,
            };
        }
        return null;
    });

    readonly includeOrgBudgetSummary = linkedSignal(() => true);
    readonly includeBudgetPlans = linkedSignal(() => true);
    readonly includeLineItems = linkedSignal(() => true);

    readonly scopeDescription = computed(() => {
        const exportContext = this.context();
        if (!exportContext) {
            return "Choose what to include in the export.";
        }
        switch (exportContext.mode) {
            case "singleOrg":
                return exportContext.organizationName
                    ? `Export data for ${exportContext.organizationName}.`
                    : "Export data for this organization.";
            case "bulkByFiscalYear":
                return exportContext.fiscalYear
                    ? `Export data for all organizations in fiscal year ${exportContext.fiscalYear}.`
                    : "Export data for all organizations in the selected fiscal year.";
            case "bulkByOrgIds": {
                const count = exportContext.organizationIds?.length ?? 0;
                return count
                    ? `Export data for ${count} selected organization${count === 1 ? "" : "s"}.`
                    : "Export data for selected organizations.";
            }
            default:
                return "Choose what to include in the export.";
        }
    });

    readonly validationMessage = computed(() => {
        const enabledKinds = OrganizationExport.KIND_REGISTRY.filter(
            (kindDef) => kindDef.implemented && this.isKindEnabled(kindDef.kind),
        );
        if (!enabledKinds.length) {
            return "Select at least one object type to export.";
        }
        if (this.isKindEnabled("budget")) {
            if (!this.selectedFiscalYear()?.itemId) {
                return "Select a fiscal year to export.";
            }
            if (!this.includeOrgBudgetSummary() && !this.includeBudgetPlans() && !this.includeLineItems()) {
                return "Select at least one budget section to export.";
            }
        }
        return "";
    });

    readonly canConfirm = computed(() => !this.validationMessage());

    readonly headerModalData = computed((): ModalData | undefined => {
        const modalParams = this.params();
        if (!modalParams) {
            return undefined;
        }
        return {
            ...modalParams,
            title: OrganizationExportModalComponent.title,
        };
    });

    isKindEnabled(kind: OrganizationExport.Kind): boolean {
        return this.kindEnabled()[kind] ?? false;
    }

    onKindChanged(kind: OrganizationExport.Kind, change: { data: boolean }): void {
        this.kindEnabled.update((current) => ({ ...current, [kind]: change.data }));
    }

    confirm(): void {
        if (!this.canConfirm()) {
            return;
        }
        const kinds = OrganizationExport.KIND_REGISTRY.filter(
            (kindDef) => kindDef.implemented && this.isKindEnabled(kindDef.kind),
        ).map((kindDef) => kindDef.kind);

        const selection: OrganizationExport.Selection = { kinds };
        if (this.isKindEnabled("budget")) {
            const fiscalYear = this.selectedFiscalYear();
            selection.budget = {
                includeOrgBudgetSummary: this.includeOrgBudgetSummary(),
                includeBudgetPlans: this.includeBudgetPlans(),
                includeLineItems: this.includeLineItems(),
            };
            selection.fiscalYearId = fiscalYear?.itemId;
            selection.fiscalYear = fiscalYear?.itemName;
        }
        this.params()?.closeModal(selection);
    }

    cancel(): void {
        this.params()?.closeModal();
    }
}
